/**
 * 内置模型头像是透明底黑剪影。模型接口不带 favicon 文件名，暗色底上会消失。
 * 只给「不透明像素几乎全黑、其余透明」的小图打标记，暗色样式据此反色；
 * 彩色头像和照片不标记。
 */

export const DARK_MARK_ATTRIBUTE = 'data-dark-mark';

const SAMPLE_CAP = 48;
const OPAQUE_ALPHA = 16;
const MARK_COVERAGE = 0.004;
const BLACK_CHANNEL = 18;
const BLACK_SHARE = 0.98;

export function isNearBlackPixels(pixels: Uint8ClampedArray, width: number, height: number): boolean {
	if (width < 2 || height < 2) return false;

	let opaque = 0;
	let black = 0;
	for (let index = 0; index < pixels.length; index += 4) {
		if (pixels[index + 3] < OPAQUE_ALPHA) continue;
		opaque += 1;
		if (
			pixels[index] <= BLACK_CHANNEL &&
			pixels[index + 1] <= BLACK_CHANNEL &&
			pixels[index + 2] <= BLACK_CHANNEL
		) {
			black += 1;
		}
	}

	const coverage = opaque / (width * height);
	return coverage >= MARK_COVERAGE && coverage < 1 && black / opaque >= BLACK_SHARE;
}

export function isNearBlackMark(image: CanvasImageSource, width: number, height: number): boolean {
	if (width < 2 || height < 2) return false;

	const canvas = document.createElement('canvas');
	const sampleWidth = Math.max(1, Math.min(SAMPLE_CAP, width));
	const sampleHeight = Math.max(1, Math.min(SAMPLE_CAP, height));
	canvas.width = sampleWidth;
	canvas.height = sampleHeight;

	const context = canvas.getContext('2d', { willReadFrequently: true });
	if (!context) return false;
	context.drawImage(image, 0, 0, sampleWidth, sampleHeight);

	return isNearBlackPixels(
		context.getImageData(0, 0, sampleWidth, sampleHeight).data,
		sampleWidth,
		sampleHeight
	);
}

export function markDarkAvatar(image: HTMLImageElement): void {
	image.removeAttribute(DARK_MARK_ATTRIBUTE);
	if (!image.complete || image.naturalWidth === 0) return;

	const width = image.naturalWidth || image.width;
	const height = image.naturalHeight || image.height;
	try {
		if (isNearBlackMark(image, width, height)) image.setAttribute(DARK_MARK_ATTRIBUTE, '');
	} catch {
		// 跨域图片读不了像素，保持原样。
	}
}

export function watchDarkAvatars(root: ParentNode = document): () => void {
	const seen = new WeakSet<HTMLImageElement>();

	const watch = (image: HTMLImageElement) => {
		if (seen.has(image)) return;
		seen.add(image);
		const apply = () => markDarkAvatar(image);
		if (image.complete) apply();
		image.addEventListener('load', apply);
		image.addEventListener('error', () => image.removeAttribute(DARK_MARK_ATTRIBUTE));
	};

	const scan = (node: ParentNode) => {
		if (node instanceof HTMLImageElement) watch(node);
		node.querySelectorAll?.('img').forEach((image) => watch(image));
	};

	scan(root);
	const observer = new MutationObserver((records) => {
		for (const record of records) {
			if (record.type === 'attributes' && record.target instanceof HTMLImageElement) {
				markDarkAvatar(record.target);
				continue;
			}
			record.addedNodes.forEach((node) => {
				if (node instanceof Element) scan(node);
			});
		}
	});
	observer.observe(root, {
		subtree: true,
		childList: true,
		attributes: true,
		attributeFilter: ['src']
	});

	return () => observer.disconnect();
}
